import { Component, Injector, Input, OnInit } from "@angular/core";
import { Message } from "../../models/message";
import { MessageService } from "../../services/message/message.service";
import * as moment from "moment";
import { ActivatedRoute, Router } from "@angular/router";
import { BaseComponent } from "../base/base.component";
import { UserService } from "../../services/user/user.service";
import { CategoryService } from "../../services/category/category.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import Swal from "sweetalert2";
import { Category } from "../../models/category";
import { User } from "../../models/user";
import { MessagesStateService } from "../../services/message/message.state.service";

enum MessageTabs {
  AllMessages = "AllMessages",
  UnreadReceivedMessages = "UnreadReceivedMessages",
  ReceivedMessages = "ReceivedMessages",
  SentMessages = "SentMessages",
}

@Component({
  selector: "app-messages",
  templateUrl: "./messages.component.html",
  styleUrls: ["./messages.component.scss"],
})
export class MessagesComponent extends BaseComponent implements OnInit {
  messagesNoFilter: Message[] = [];
  sentMessages: Message[] = [];
  messages: Message[] = [];
  messagesToRead: number = 0;
  messagesSent: number = 0;
  newMessage: Message = new Message();
  messageId: string | undefined;
  messagesMenuActive: MessageTabs | undefined;
  totalMessagesCount: number = 0;
  loadingMessages: boolean = true;

  entityMessageType: string = "2";
  selectedParentCategory: Category[] = [];
  selectedSubCategory: Category[] = [];
  categoryDropdownSettings = { idField: "id", textField: "name" };
  recipientTypes = [
    { value: "2", text: "Tutti", icon: "bi-people" },
    { value: "0", text: "Categoria", icon: "bi-folder2" },
    { value: "1", text: "Singolo utente", icon: "bi-person" },
  ];
  users: User[] = [];
  usersDropdownSettings: any = {};
  fullName: string = "";

  parentCategories: Category[] = [];
  categories: Category[] = [];

  companyId: string = localStorage.getItem("wisper_companyId") ?? "";

  wysiwygConfig = {
    setReadOnly: false,
    readonly: false,
    toolbar: true,
  };

  // Seeded from the login data so role-based UI doesn't flicker while /users/me loads
  isAdmin: boolean = localStorage.getItem("wisper_userGroup") === "admin";
  isCompany: boolean = localStorage.getItem("wisper_userGroup") === "companyManager";

  messageTarget: any = [
    { value: "agent", text: "Agenti" },
    { value: "companyManager", text: "Società" },
  ];

  currentPage: number = 0;
  itemsPerPage: number = 20;
  pagesCount: number = 0;

  constructor(
    public messageService: MessageService,
    public categoryService: CategoryService,
    public userService: UserService,
    public modal: NgxSmartModalService,
    public route: ActivatedRoute,
    public router: Router,
    public serviceInjector: Injector,
    private messagesStateService: MessagesStateService,
  ) {
    super(serviceInjector);
  }

  async ngOnInit(): Promise<void> {
    const storedTab = this.messagesStateService.getActiveTab();
    this.messagesMenuActive =
      storedTab !== undefined
        ? (Object.values(MessageTabs)[storedTab] as MessageTabs)
        : MessageTabs.AllMessages;
    console.log("[MessagesComponent] Tab attivo ripristinato:", this.messagesMenuActive);

    this.usersDropdownSettings = {
      singleSelection: true,
      idField: "id",
      textField: "fullName",
      allowSearchFilter: true,
      closeDropDownOnSelection: true,
      clearSearchFilter: true,
      searchPlaceholderText: "Cerca...",
    };

    // Show cached messages right away and refresh in the background, without blocking the page
    this.loadingMessages = !this.messagesStateService.received;
    if (!this.loadingMessages) this.applyMessages();
    void this.loadMessages();

    let userResponse = await this.userService.getCurrentUser();
    if (userResponse.success) {
      this.isAdmin = userResponse.data?.userGroupId === "admin";
      this.isCompany = userResponse.data?.userGroupId === "companyManager";
    }

    if (
      this.route.snapshot.params["messageId"] &&
      this.isUUID(this.route.snapshot.params["messageId"])
    ) {
      this.messageId = this.route.snapshot.params["messageId"];
    } else {
      this.isAdmin
        ? this.router.navigate(["/admins/messages"])
        : this.isCompany
          ? this.router.navigate(["/companies/messages"])
          : this.router.navigate(["/users/messages"]);
    }

    if (this.isCompany) {
      this.newMessage["targetUserGroup"] = "user";
      await this.loadCategories();
    }
  }

  async loadMessages() {
    const [received, sent] = await Promise.all([
      this.messageService.getAllMessages(),
      this.messageService.getSentMessages(),
    ]);
    this.messagesStateService.received = received?.data ?? [];
    this.messagesStateService.sent = sent?.data ?? [];
    this.applyMessages();
    this.loadingMessages = false;
  }

  applyMessages() {
    this.messagesNoFilter = this.messagesStateService.received ?? [];
    this.sentMessages = this.messagesStateService.sent ?? [];
    this.messagesToRead = this.messagesNoFilter.filter((message: Message) => !message.seen).length;
    this.messagesSent = this.sentMessages.length;
    this.totalMessagesCount = this.messagesNoFilter.length + this.sentMessages.length;
    this.setMessagesToShow(this.messagesMenuActive);
  }

  async loadCategories() {
    this.categories = await this.categoryService.getCategories(this.companyId);
    console.log("categories: ", this.categories);
    this.parentCategories = this.categories.filter(
      (category: Category) => category.parentId === null,
    );
    console.log("parent cat: ", this.parentCategories);
  }

  get subCategories(): Category[] {
    return this.categories.filter(
      (category: Category) =>
        !!this.selectedParentCategory.length && category.parentId === this.selectedParentCategory[0].id,
    );
  }

  get hasRecipient(): boolean {
    if (!this.isCompany || this.entityMessageType === "2") return true;
    return this.entityMessageType === "0" ? !!this.newMessage.categoryId : !!this.newMessage.userId;
  }

  setRecipientType(type: string) {
    this.entityMessageType = type;
    this.selectedParentCategory = [];
    this.selectedSubCategory = [];
    this.newMessage.categoryId = undefined;
    this.newMessage.userId = undefined;
    if (type === "1") this.onFilterChange("");
  }

  // Picking a parent targets the whole category until a subcategory is chosen
  onParentCategoryChange() {
    this.selectedSubCategory = [];
    this.newMessage.categoryId = this.selectedParentCategory[0]?.id;
  }

  onSubCategoryChange() {
    this.newMessage.categoryId =
      this.selectedSubCategory[0]?.id ?? this.selectedParentCategory[0]?.id;
  }

  setMessagesMenuActive(tabIndex: number) {
    this.messagesMenuActive = Object.values(MessageTabs)[tabIndex];
    this.messagesStateService.setActiveTab(tabIndex);
    this.setMessagesToShow(this.messagesMenuActive);
  }

  setMessagesToShow(tab: MessageTabs | undefined) {
    if (!tab) return;
    if (tab === MessageTabs.AllMessages) {
      this.messages = [...this.messagesNoFilter, ...this.sentMessages];
    } else if (tab === MessageTabs.SentMessages) {
      this.messages = [...this.sentMessages];
    } else if (tab === MessageTabs.UnreadReceivedMessages) {
      this.messages = this.messagesNoFilter.filter((message: Message) => !message.seen);
    } else if (tab === MessageTabs.ReceivedMessages) {
      this.messages = [...this.messagesNoFilter];
    }
    // Update pagination
    this.pagesCount = Math.ceil(this.messages.length / this.itemsPerPage);
    this.currentPage = 0;
    console.log("[MessagesComponent] Messaggi visualizzati per la tab", tab, ":", this.messages);
    console.log("[MessagesComponent] Pagina corrente reimpostata a:", this.currentPage);
  }

  openNewMessageModal() {
    this.modal.getModal("newMessageModal").open();
  }

  closeNewMessageModal() {
    this.newMessage = new Message();
    if (this.isCompany) this.newMessage["targetUserGroup"] = "user";
    this.users = [];
    this.entityMessageType = "2";
    this.selectedParentCategory = [];
    this.selectedSubCategory = [];
    this.modal.getModal("newMessageModal").close();
  }

  async sendMessage(newMessage: Message) {
    console.log(newMessage);
    this.showSpinner();
    let response = await this.messageService.createMessage(newMessage);
    this.hideSpinner();

    if (response?.success) {
      this.showSuccessMessage(
        response.data === 1 ? "Messaggio inviato a 1 destinatario" : `Messaggio inviato a ${response.data} destinatari`,
      );
      this.closeNewMessageModal();
      await this.loadMessages();
    } else if (response?.code === "NO_RECIPIENTS") {
      // Keep the dialog open so the user can pick other recipients without losing the text
      this.showErrorMessage("Nessun destinatario trovato per la selezione");
    } else {
      this.closeNewMessageModal();
      this.showErrorMessage("Errore durante l'invio del messaggio");
    }
  }

  async onFilterChange(filter: any) {
    // Empty search preloads a short list instead of every user of the company
    let users: User[] = await this.userService.searchUsers(filter, this.companyId, filter ? undefined : 10);
    if (users.length !== 0) {
      users.forEach((user) => {
        user.fullName = user.firstName + " " + user.lastName + " " + user.email;
      });
      this.users = users;
    } else {
      this.users = [];
    }
  }

  onUserSelect(item: any) {
    this.newMessage.userId = item.id;
  }

  get paginatedMessages(): Message[] {
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.messages.slice(startIndex, endIndex);
  }

  previousPagePagination() {
    if (this.currentPage > 0) {
      this.currentPage--;
    }
  }

  nextPagePagination() {
    if (this.currentPage < this.pagesCount - 1) {
      this.currentPage++;
    }
  }

  loadPage(page: number) {
    this.currentPage = page;
  }

  navigateToCategory(tabIndex: number) {
    console.log("navigateToCategory chiamato con:", tabIndex);
    this.messageId = undefined;
    this.setMessagesMenuActive(tabIndex);
  }

  currentPageGroup: number = 0;

  getPagesArray(): number[] {
    const totalPages = this.pagesCount;
    const pagesToShow = 5;
    const startPage = this.currentPageGroup * pagesToShow;
    const endPage = Math.min(startPage + pagesToShow, totalPages);

    const pages: number[] = [];
    for (let i = startPage; i < endPage; i++) {
      pages.push(i + 1);
    }

    if (startPage > 0) {
      pages.unshift(-1);
    }
    if (endPage < totalPages) {
      pages.push(-2);
    }

    return pages;
  }

  loadPageGroup(direction: "prev" | "next") {
    const totalGroups = Math.ceil(this.pagesCount / 5);
    if (direction === "prev" && this.currentPageGroup > 0) {
      this.currentPageGroup--;
    } else if (direction === "next" && this.currentPageGroup < totalGroups - 1) {
      this.currentPageGroup++;
    }
  }
}
